import React, { useEffect, useMemo, useState } from "react";
import ReadAssessment from "./ReadAssessment";
import { useDispatch, useSelector } from "react-redux";
import { promptResponse } from "../../store/promptSlice";
import ListenAssessment from "./ListenAssessment";
import ParagraphAssessment from "./ParagraphAssessment";
import { setPopup } from "../../store/popupSlice";

function Assess() {
  const [currentPage, setCurrentPage] = useState("assessPage");
  const loading = useSelector((state) => state.prompt.loading);
  const dispatch = useDispatch();
  const [response, setResponse] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const isMobile =
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
        navigator.userAgent
      ) || window.innerWidth < 768;

    if (isMobile) {
      dispatch(
        setPopup({
          message:
            "📱 For best experience, please use a laptop or desktop to complete the assessment.",
          type: "error",
        })
      );
    }
  }, [dispatch]);

  const startAssessment = async () => {
    const result = await dispatch(promptResponse());
    let statements = result?.payload?.reply ?? [];

    if (!Array.isArray(statements)) {
      statements = [String(statements)];
    }

    setResponse(statements);
    setCurrentPage("read");
    setTimeLeft(60);
  };

  const goToListen = () => {
    setCurrentPage("listen");
    setTimeLeft(120);
  };

  const goToParagraph = () => {
    setCurrentPage("paragraph");
    setTimeLeft(600);
  };

  // ✅ Memoize slices so their reference is stable across re-renders
  const readItems = useMemo(() => response.slice(0, 5), [response]);
  const listenItems = useMemo(() => response.slice(5), [response]);

  if (currentPage === "read") {
    return (
      <ReadAssessment
        Exit={goToListen}
        response={readItems}
        timeLeft={timeLeft}
        setTimeLeft={setTimeLeft}
      />
    );
  }

  if (currentPage === "listen") {
    return (
      <ListenAssessment
        Exit={goToParagraph}
        response={listenItems}
        timeLeft={timeLeft}
        setTimeLeft={setTimeLeft}
      />
    );
  }

  if (currentPage === "paragraph") {
    return (
      <ParagraphAssessment
        Exit={() => setCurrentPage("assessPage")}
        // response={listenItems}
        timeLeft={timeLeft}
        setTimeLeft={setTimeLeft}
      />
    );
  }

  return (
    <div className="min-h-screen mt-24 flex items-center justify-center bg-gray-50">
      <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl p-8">
        <h1 className="text-2xl font-bold text-center text-indigo-600 mb-4">
          Welcome to the Assessment Test
        </h1>
        <p className="text-gray-600 text-center mb-6">
          Please read the instructions carefully before you begin.
        </p>

        <div className="text-gray-700 space-y-3">
          <p>
            1. The assessment is divided into <strong>3 sections</strong>:
          </p>
          <p className="pl-4">• Reading Assessment – 1 minute</p>
          <p className="pl-4">• Listening Assessment – 2 minutes</p>
          <p className="pl-4">• Paragraph Assessment – 6 minutes</p>

          <p>
            2. Each section is strictly time-bound. Once the timer starts, it
            cannot be paused or reset.
          </p>
          <p>3. Do not refresh, close, or switch tabs during the assessment.</p>
          <p>
            4. Ensure you are in a quiet environment with a stable internet
            connection.
          </p>
          <p>
            5. Use headphones for the listening assessment to avoid
            distractions.
          </p>
          <p>6. Read each question carefully before answering.</p>
          <p>
            7. Responses should be your own. Any form of malpractice may lead to
            disqualification.
          </p>
          <p>
            8. You may finish a section early by clicking{" "}
            <strong>Finish Test</strong>, but you cannot return once you
            proceed.
          </p>
          <p>
            9. Make sure your microphone and speakers are working properly
            before starting.
          </p>
          <p>
            10. Keep your device fully charged or connected to power to avoid
            interruptions.
          </p>
        </div>

        <div className="mt-6 bg-yellow-100 text-yellow-700 px-4 py-3 rounded-lg">
          ⚠️ Please note: Failure to follow the above instructions may result in
          termination of the test.
        </div>

        <div className="flex justify-center mt-8">
          <button
            type="button"
            onClick={startAssessment}
            disabled={loading}
            className="w-full text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 font-bold rounded-lg text-lg px-6 py-3.5 transition-transform transform hover:scale-105 active:scale-100 shadow-md hover:shadow-lg"
          >
            {loading
              ? "Assessment will start shortly. Please wait...."
              : "Start Assessment"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Assess;
